import { ModifierArguments, ModifierPhases, Options, Rect } from '@popperjs/core';
import maxSize from 'popper-max-size-modifier';
import React from 'react';
import { UsePopperModifiersProps } from './PopoverTypes';

export function usePopperModifiers({
    arrowElement,
    popperElement,
    referenceElement,
    offset,
    matchWidth,
    verticalOverflow,
}: UsePopperModifiersProps): Options['modifiers'] {
    const applyMaxSize = React.useMemo(
        () => ({
            name: 'applyMaxSize',
            enabled: true,
            phase: 'beforeWrite' as ModifierPhases,
            requires: ['maxSize'],
            fn({ state }: ModifierArguments<Options>) {
                const { height } = state.modifiersData.maxSize;
                state.elements.popper.style.maxHeight = `${height}px`;
                state.elements.popper.style.overflowY = 'auto';
            },
        }),
        []
    );
    const sameWidth = React.useMemo(
        () => ({
            name: 'sameWidth',
            enabled: true,
            phase: 'beforeWrite' as ModifierPhases,
            requires: ['computeStyles'],
            fn: ({ state }: ModifierArguments<Options>) => {
                state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            effect: ({ state }: ModifierArguments<Options>) => {
                // Work around for types not being 100% correct
                const { offsetWidth } = state?.elements?.reference as unknown as { offsetWidth: number };
                state.elements.popper.style.width = `${offsetWidth}px`;
            },
        }),
        []
    );

    const arrowModifier = React.useMemo(
        () => ({
            name: 'arrow',
            options: {
                element: arrowElement,
                padding: ({ popper: popperElement, reference: referenceElement }: { popper: Rect; reference: Rect }) =>
                    popperElement.width / referenceElement.width,
            },
        }),
        [popperElement, referenceElement, arrowElement]
    );
    const modifiers = [
        {
            name: 'offset',
            enabled: true,
            options: {
                offset: offset,
            },
        },
        arrowModifier,
    ] as Options['modifiers'];
    if (verticalOverflow) {
        modifiers.push(maxSize, applyMaxSize);
    }
    if (matchWidth) {
        modifiers.push(sameWidth);
    }
    return modifiers;
}
